import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { apiError } from '@/lib/apiError';
import {
  createUser,
  listRoles,
  listUsers,
  updateUser,
} from '@/api/admin';

interface Props {
  open: boolean;
  userId: number | null;
  onClose: () => void;
}

export function UserFormModal({ open, userId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'fullName' | 'roleId' | 'username' | 'password'>();
  const isEdit = userId !== null;
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [departments, setDepartments] = useState<string[]>([]);
  const [deptInput, setDeptInput] = useState('');
  const [roleId, setRoleId] = useState('');
  const deptRef = useRef<HTMLInputElement>(null);

  const rolesQ = useQuery({ queryKey: ['admin-roles'], queryFn: listRoles, enabled: open });
  const usersQ = useQuery({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && usersQ.data) {
      const u = usersQ.data.find((x) => x.id === userId);
      if (u) {
        setUsername(u.username);
        setEmail(u.email ?? '');
        setFullName(u.fullName);
        setPhone(u.phone ?? '');
        setDepartments(u.department ? u.department.split(',').map((d) => d.trim()).filter(Boolean) : []);
        setRoleId(String(u.roleId));
      }
    } else {
      setUsername('');
      setEmail('');
      setFullName('');
      setPassword('');
      setPhone('');
      setDepartments([]);
      setDeptInput('');
      setRoleId('');
    }
  }, [open, isEdit, userId, usersQ.data]);

  function addDept(raw: string) {
    const val = raw.trim();
    if (!val) return;
    if (!departments.includes(val)) setDepartments((d) => [...d, val]);
    setDeptInput('');
  }

  function removeDept(dept: string) {
    setDepartments((d) => d.filter((x) => x !== dept));
  }

  const onErr = (e: unknown) => {
    toast.push(apiError(e, 'Failed'), 'error');
  };

  const createMut = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      toast.push('User created', 'success');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: onErr,
  });
  const updateMut = useMutation({
    mutationFn: (payload: Parameters<typeof updateUser>[1]) =>
      updateUser(userId as number, payload),
    onSuccess: () => {
      toast.push('User updated', 'success');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: onErr,
  });

  function submit() {
    const ok = fe.validate({
      fullName: !fullName.trim() && 'Full name is required',
      roleId: !roleId && 'Select a role',
      username: !isEdit && !username.trim() && 'Username is required',
      password: !isEdit && password.length < 8 && 'Password must be at least 8 characters',
    });
    if (!ok) return;
    // commit any pending text in the dept input
    const finalDepts = deptInput.trim()
      ? [...new Set([...departments, deptInput.trim()])]
      : departments;
    const departmentStr = finalDepts.join(', ') || undefined;

    if (!isEdit) {
      createMut.mutate({
        username,
        email,
        fullName,
        password,
        roleId: Number(roleId),
        phone: phone || undefined,
        department: departmentStr,
      });
    } else {
      updateMut.mutate({
        fullName,
        email,
        phone: phone || undefined,
        department: departmentStr,
        roleId: Number(roleId),
      });
    }
  }

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit User' : 'New User'}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={createMut.isPending || updateMut.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3">
        <FormGroup label="Full Name *" error={fe.errors.fullName}>
          <Input
            invalid={fe.has('fullName')}
            value={fullName}
            onChange={(e) => { setFullName(e.target.value); fe.clear('fullName'); }}
          />
        </FormGroup>
        <div className="grid grid-cols-2 gap-3">
          <FormGroup label="Username *" error={fe.errors.username}>
            <Input
              invalid={fe.has('username')}
              value={username}
              onChange={(e) => { setUsername(e.target.value); fe.clear('username'); }}
              disabled={isEdit}
            />
          </FormGroup>
          <FormGroup label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormGroup>
        </div>
        {!isEdit && (
          <FormGroup label="Password * (min 8 chars)" error={fe.errors.password}>
            <Input
              type="password"
              invalid={fe.has('password')}
              value={password}
              onChange={(e) => { setPassword(e.target.value); fe.clear('password'); }}
            />
          </FormGroup>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FormGroup label="Phone">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </FormGroup>
          <FormGroup label="Department">
            {/* Tag input */}
            <div
              className="flex min-h-[38px] flex-wrap items-center gap-1 rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] px-2 py-1 cursor-text"
              onClick={() => deptRef.current?.focus()}
            >
              {departments.map((d) => (
                <span
                  key={d}
                  className="flex items-center gap-1 rounded bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent"
                >
                  {d}
                  <button
                    type="button"
                    className="leading-none hover:text-red-500"
                    onClick={(e) => { e.stopPropagation(); removeDept(d); }}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                ref={deptRef}
                value={deptInput}
                onChange={(e) => setDeptInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addDept(deptInput);
                  } else if (e.key === 'Backspace' && !deptInput && departments.length) {
                    setDepartments((d) => d.slice(0, -1));
                  }
                }}
                onBlur={() => addDept(deptInput)}
                placeholder={departments.length ? '' : 'Type & press Enter'}
                className="min-w-[80px] flex-1 bg-transparent text-sm outline-none"
              />
            </div>
          </FormGroup>
        </div>
        <FormGroup label="Role *" error={fe.errors.roleId}>
          <Select
            invalid={fe.has('roleId')}
            value={roleId}
            onChange={(e) => { setRoleId(e.target.value); fe.clear('roleId'); }}
          >
            <option value="">— Select Role —</option>
            {(rolesQ.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.code})
              </option>
            ))}
          </Select>
        </FormGroup>
      </div>
    </Modal>
  );
}
